import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, RotateCcw } from 'lucide-react';
import { CTPAnsweredQuestion, CTPVoteResults } from '../../types/compareThePair';
import { CTPSummaryTable } from './CTPSummaryTable';
import { get } from '../../utils/api';
import { useResetCountdown } from '../../hooks/useResetCountdown';

interface CTPResultsBannerProps {
  score: number;
  answers: boolean[];
  nameA: string;
  nameB: string;
  industry: string;
  gameDate: string;
  answeredQuestions: CTPAnsweredQuestion[];
  symbolA: string;
  symbolB: string;
  logoUrlA: string | null;
  logoUrlB: string | null;
  onReview?: () => void;
}

function formatGameDate(gameDate: string): string {
  const [y, m, d] = gameDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function getScoreMessage(score: number): string {
  if (score === 10) return 'Perfect Score!';
  if (score >= 8) return 'Excellent!';
  if (score >= 6) return 'Good job!';
  if (score >= 4) return 'Not bad!';
  return 'Keep learning!';
}

export const CTPResultsBanner: React.FC<CTPResultsBannerProps> = ({
  score,
  answers,
  nameA,
  nameB,
  industry,
  gameDate,
  answeredQuestions,
  symbolA,
  symbolB,
  logoUrlA,
  logoUrlB,
  onReview,
}) => {
  const [copied, setCopied] = useState(false);
  const { hours, minutes } = useResetCountdown();
  const [voteResults, setVoteResults] = useState<CTPVoteResults | null>(null);

  // Fetch vote results for the poll line in share text
  useEffect(() => {
    get<CTPVoteResults>(`/api/compare-the-pair/votes/${gameDate}`)
      .then(res => setVoteResults(res.data))
      .catch(() => {});
  }, [gameDate]);

  const emojiGrid = useMemo(() => {
    return answers.map(correct => correct ? '\u{1F7E9}' : '\u{1F7E5}').join('');
  }, [answers]);

  const shareText = useMemo(() => {
    const dateLine = `Wallstreetle Compare the Pair ${formatGameDate(gameDate)}`;
    const lines = [dateLine, `${nameA} vs ${nameB}`, emojiGrid, `My Score: ${score}/10`];
    if (voteResults && voteResults.totalVotes > 0) {
      const pctA = Math.round((voteResults.votesA / voteResults.totalVotes) * 100);
      const pctB = 100 - pctA;
      lines.push(`Latest Poll Results: ${symbolA} ${pctA}% vs ${symbolB} ${pctB}%`);
    }
    lines.push('https://wallstreetle.com/compare-the-pair');
    return lines.join('\n');
  }, [gameDate, nameA, nameB, emojiGrid, score, symbolA, symbolB, voteResults]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-4 overflow-y-auto animate-fade-up">
      {/* Score */}
      <div className="mb-2">
        <span
          className="text-6xl font-bold font-mono"
          style={{ color: 'var(--ctp-accent)' }}
        >
          {score}
        </span>
        <span
          className="text-2xl font-bold"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          /10
        </span>
      </div>

      <p
        className="text-lg font-semibold mb-3"
        style={{ color: 'hsl(var(--muted-foreground))' }}
      >
        {getScoreMessage(score)}
      </p>

      {/* Emoji grid */}
      <div className="text-2xl tracking-widest mb-2">
        {emojiGrid}
      </div>

      <p
        className="text-sm mb-3"
        style={{ color: 'hsl(var(--muted-foreground))' }}
      >
        {nameA} vs {nameB} &middot; {industry}
      </p>

      {/* Poll results bar */}
      {voteResults && voteResults.totalVotes > 0 && (() => {
        const pctA = Math.round((voteResults.votesA / voteResults.totalVotes) * 100);
        return (
          <div className="w-full max-w-sm mb-5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-semibold shrink-0 w-10 text-right" style={{ color: 'hsl(var(--muted-foreground))' }}>
                {symbolA}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden flex" style={{ backgroundColor: 'hsl(var(--muted))' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.max(pctA, 5)}%`, backgroundColor: 'var(--ctp-accent)' }}
                />
              </div>
              <span className="text-xs font-mono font-semibold shrink-0 w-10 text-left" style={{ color: 'hsl(var(--muted-foreground))' }}>
                {symbolB}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Action buttons */}
      <div className="flex flex-col items-center gap-3">
        <button
          onClick={handleCopy}
          className="flex items-center gap-2 px-10 py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.97]"
          style={{
            backgroundColor: copied ? 'var(--ctp-correct)' : 'var(--ctp-accent)',
            color: '#fff',
          }}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Share Results'}
        </button>
        {onReview && (
          <button
            onClick={onReview}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.97]"
            style={{
              backgroundColor: 'hsl(var(--muted))',
              color: 'var(--ctp-text-secondary)',
              border: '1px solid var(--ctp-card-border)',
            }}
          >
            <RotateCcw className="w-4 h-4" />
            Review
          </button>
        )}
      </div>

      {/* Countdown */}
      <p
        className="mt-5 text-sm"
        style={{ color: 'hsl(var(--muted-foreground))' }}
      >
        Next pair in {hours}h {minutes}m
      </p>

      {/* Other games */}
      <div className="mt-4 mb-4 flex justify-center gap-3">
        <Link
          to="/"
          className="text-sm font-medium px-5 py-2.5 rounded-full border transition-opacity hover:opacity-80"
          style={{
            color: 'var(--ctp-text-secondary)',
            borderColor: 'hsl(var(--border))',
          }}
        >
          Play Classic
        </Link>
        <Link
          to="/trivia-rush"
          className="text-sm font-medium px-5 py-2.5 rounded-full border transition-opacity hover:opacity-80"
          style={{
            color: 'var(--ctp-text-secondary)',
            borderColor: 'hsl(var(--border))',
          }}
        >
          Play Trivia Rush
        </Link>
      </div>

      {/* Inline Summary Table */}
      {answeredQuestions.length > 0 && (
        <div className="w-full max-w-lg mx-auto mb-4">
          <CTPSummaryTable
            answeredQuestions={answeredQuestions}
            nameA={nameA}
            nameB={nameB}
            symbolA={symbolA}
            symbolB={symbolB}
            logoUrlA={logoUrlA}
            logoUrlB={logoUrlB}
          />
        </div>
      )}
    </div>
  );
};
